import { delay, put, call } from "redux-saga/effects";
import { fetchCombinedAutocomplete } from "../../../api/v0/search/fetchCombinedAutocomplete";
import Logger from "../../../utils/logger";
import { actions } from "../actions";
import {
  CombinedAutocompleteResponse,
  CombinedSearchGroup,
  CombinedSearchGroupType,
  CombinedSearchSuggestion,
} from "../types";

/**
 * Scala sealed trait case objects serialize as {"TypeName": "Value"} (e.g. {"CombinedSearchResultType": "Destination"}).
 * This normalizer extracts the actual value from either format.
 */
function normalizeEnumValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null) {
    const entries = Object.values(value as Record<string, unknown>);
    const firstValue = entries[0];
    if (typeof firstValue === "string") return firstValue;
    return Object.keys(value as Record<string, unknown>)[0] ?? "Unknown";
  }
  return "Unknown";
}

function normalizeGroups(rawGroups: unknown[]): CombinedSearchGroup[] {
  return (rawGroups || []).map((rawGroup: any) => ({
    groupType: normalizeEnumValue(
      rawGroup.groupType
    ) as CombinedSearchGroupType,
    suggestions: (rawGroup.suggestions || []).map(
      (s: any): CombinedSearchSuggestion => ({
        id: s.id,
        label: s.label,
        suggestionType: normalizeEnumValue(s.suggestionType),
        subLabel: s.subLabel,
        thumbnailUrl: s.thumbnailUrl,
        reviewAverage: s.reviewAverage,
        reviewCount: s.reviewCount,
        activityCount: s.activityCount,
        trackingPropertiesV2: s.trackingPropertiesV2,
        destinationId: s.destinationId,
        destinationName: s.destinationName,
      })
    ),
  }));
}

/**
 * Drop any non-Destination suggestion that doesn't have positive evidence of
 * belonging to the scoped destination. The BE's destinationId-based scoping
 * has been observed to leak cross-destination content (e.g. searching
 * "Colosseum" on a Las Vegas shop returned Rome's Colosseum tours), and many
 * leaked suggestions come back with no `destinationId` and no
 * `destinationName` at all — a lenient filter that keeps untagged
 * suggestions can't catch them.
 *
 * A suggestion is kept only when one of the following is true:
 *   - `destinationId` matches `scopedDestinationId`, or
 *   - `destinationName` contains `scopedDestinationName` (case-insensitive),
 *     so scope "Rome" matches a suggestion tagged "Rome, Italy" but scope
 *     "Paris, France" does NOT match a suggestion tagged just "France".
 *
 * Untagged suggestions are dropped. The contract for the shop / availability
 * pages is that categories, attractions and products must be scoped to the
 * current destination — a suggestion that can't prove its destination has no
 * place there.
 */
export function enforceDestinationScope(
  groups: CombinedSearchGroup[],
  scopedDestinationId: string,
  scopedDestinationName: string | undefined
): CombinedSearchGroup[] {
  const scopedName = scopedDestinationName?.toLowerCase().trim();

  const matchesScope = (s: CombinedSearchSuggestion): boolean => {
    if (s.destinationId === scopedDestinationId) return true;
    if (!scopedName || !s.destinationName) return false;
    return s.destinationName.toLowerCase().trim().includes(scopedName);
  };

  return groups
    .map((group) =>
      group.groupType === "Destination"
        ? group
        : { ...group, suggestions: group.suggestions.filter(matchesScope) }
    )
    .filter(
      (group) =>
        group.groupType === "Destination" || group.suggestions.length > 0
    );
}

/**
 * Drop suggestions whose `activityCount` is explicitly zero. Categories,
 * attractions and destinations carry an activity count from the BE — when
 * it's zero, navigating into the result lands the user on an empty
 * availability page (a known bad UX). Products are individual offerings and
 * don't carry a meaningful count, so they're skipped. Suggestions where
 * `activityCount` is undefined are kept (we can't tell, default to showing).
 */
export function dropZeroActivitySuggestions(
  groups: CombinedSearchGroup[]
): CombinedSearchGroup[] {
  return groups
    .map((group) => {
      if (group.groupType === "Product") return group;
      return {
        ...group,
        suggestions: group.suggestions.filter((s) => s.activityCount !== 0),
      };
    })
    .filter((group) => group.suggestions.length > 0);
}

// Cache resolved destination IDs by location label to avoid re-resolving on every keystroke
const resolvedDestinationIdCache = new Map<string, string>();

export function* fetchCombinedAutocompleteSaga(
  action: actions.IFetchCombinedAutocomplete
) {
  yield delay(300);
  try {
    let { destinationId } = action.request;

    // If no destinationId but a locationLabel is provided (e.g., shop page),
    // resolve it via a quick combined autocomplete lookup for the location name.
    // This avoids needing the legacy autocomplete/experiences endpoint.
    if (!destinationId && action.request.locationLabel) {
      const cachedId = resolvedDestinationIdCache.get(
        action.request.locationLabel
      );
      if (cachedId) {
        destinationId = cachedId;
      } else {
        try {
          const locationResponse: CombinedAutocompleteResponse = yield call(
            fetchCombinedAutocomplete,
            { query: action.request.locationLabel }
          );
          const locationGroups = normalizeGroups(locationResponse.groups);
          const destGroup = locationGroups.find(
            (g) => g.groupType === "Destination"
          );
          if (destGroup && destGroup.suggestions.length > 0) {
            // BE leaves destinationId empty on Destination suggestions —
            // fall back to id (matches fetchTripDestinationsSaga.ts).
            const first = destGroup.suggestions[0];
            destinationId = first.destinationId || first.id || undefined;
            if (destinationId) {
              resolvedDestinationIdCache.set(
                action.request.locationLabel,
                destinationId
              );
            }
          }
        } catch {
          // Failed to resolve — proceed without scoping
        }
      }
    }

    const requestWithResolvedDest = {
      ...action.request,
      destinationId,
      locationLabel: undefined, // Don't send locationLabel to the API
    };

    const response: CombinedAutocompleteResponse = yield call(
      fetchCombinedAutocomplete,
      requestWithResolvedDest
    );
    let groups = normalizeGroups(response.groups);

    if (destinationId) {
      groups = enforceDestinationScope(
        groups,
        destinationId,
        action.request.locationLabel
      );
    }

    // When searching within a destination (scoped), the API suppresses destination
    // results. Fetch destination matches separately so the user can still navigate
    // to a different destination from the availability/shop page search.
    if (destinationId) {
      try {
        const unscopedResponse: CombinedAutocompleteResponse = yield call(
          fetchCombinedAutocomplete,
          { query: action.request.query }
        );
        const unscopedGroups = normalizeGroups(unscopedResponse.groups);
        const destGroup = unscopedGroups.find(
          (g) => g.groupType === "Destination"
        );
        if (destGroup && destGroup.suggestions.length > 0) {
          groups = [destGroup, ...groups];
        }
      } catch {
        // Failed to fetch destinations — proceed with scoped results only
      }
    }

    // If the initial results have a single destination and no categories/attractions,
    // fire a follow-up call scoped to that destination to fetch its categories
    if (!destinationId) {
      const destinationGroup = groups.find(
        (g) => g.groupType === "Destination"
      );
      const hasCategoryOrAttraction = groups.some(
        (g) =>
          (g.groupType === "Category" || g.groupType === "Attraction") &&
          g.suggestions.length > 0
      );

      if (
        destinationGroup &&
        destinationGroup.suggestions.length === 1 &&
        !hasCategoryOrAttraction
      ) {
        const topDestination = destinationGroup.suggestions[0];
        // Match the resolution pattern above: BE leaves destinationId empty
        // on Destination suggestions, so fall back to id.
        const followUpDestinationId =
          topDestination.destinationId || topDestination.id;
        if (followUpDestinationId) {
          try {
            const scopedResponse: CombinedAutocompleteResponse = yield call(
              fetchCombinedAutocomplete,
              {
                query: action.request.query,
                destinationId: followUpDestinationId,
              }
            );
            const scopedGroups = enforceDestinationScope(
              normalizeGroups(scopedResponse.groups),
              followUpDestinationId,
              topDestination.destinationName || topDestination.label
            );

            // Merge scoped categories/attractions into the original results
            const scopedCategoryGroups = scopedGroups.filter(
              (g) =>
                (g.groupType === "Category" || g.groupType === "Attraction") &&
                g.suggestions.length > 0
            );
            if (scopedCategoryGroups.length > 0) {
              groups = [...groups, ...scopedCategoryGroups];
            }
          } catch {
            // Follow-up call failed — proceed with original results
          }
        }
      }
    }

    yield put(
      actions.setCombinedAutocompleteResults(
        dropZeroActivitySuggestions(groups)
      )
    );
  } catch (e) {
    Logger.debug(e);
    yield put(actions.setCombinedAutocompleteError(true));
  }
}
